import { useT } from "../../i18n/hooks.ts";
import type { ProjectSeries } from "../../model/dailySeries.ts";
import type { SubBasinLayer } from "../../model/gisLayer.ts";
import {
  assignSurfaceOutletInBulk,
  hasUnreplicatedZones,
  replicateZonesAsOutletPoints,
} from "../../model/projectEntities.ts";
import type { EvalHidProject } from "../../model/types.ts";
import { AquifersForm, AquifersTable } from "../AquiferCatalog.tsx";
import { InfoIcon } from "../InfoIcon.tsx";
import { OutletPointsForm, OutletPointsTable } from "../OutletPointCatalog.tsx";
import { TopologyAssignmentSection } from "../TopologyAssignmentSection.tsx";

/**
 * Add/edit/delete outlet points (simple table, like PT_Desag.vb) — the
 * wizard's own section (`Step3Outlets`, with aquifers below), under the
 * step's own "3. ..." title, so it doesn't need one of its own; the editor
 * tab (`OutletPointsPanel.tsx`) composes OutletPointsForm/Table directly
 * instead, since IT needs its own title (no step heading above it there).
 * `inline` on the table: see OutletPointsTable's own comment.
 */
function OutletPointsSection({
  project,
  onChange,
}: {
  project: EvalHidProject;
  onChange: (project: EvalHidProject) => void;
}) {
  return (
    <>
      <OutletPointsForm project={project} onChange={onChange} />
      <OutletPointsTable project={project} onChange={onChange} inline />
    </>
  );
}

/**
 * Add/edit/delete aquifers (simple table, like Acuiferos.vb) — the
 * wizard's own section, same reason as `OutletPointsSection`. Exported:
 * Step4Topology.tsx uses it too (aquifers moved there — see its own header
 * comment for why).
 */
export function AquifersSection({
  project,
  onChange,
  series,
  onChangeSeries,
}: {
  project: EvalHidProject;
  onChange: (project: EvalHidProject) => void;
  series?: ProjectSeries;
  onChangeSeries?: (series: ProjectSeries) => void;
}) {
  return (
    <>
      <AquifersForm project={project} onChange={onChange} />
      <AquifersTable
        project={project}
        onChange={onChange}
        series={series}
        onChangeSeries={onChangeSeries}
        inline
      />
    </>
  );
}

/**
 * Step 3: outlet points — create them and, right below, which one each
 * sub-basin drains to (like PT_Desag.vb + AsigPDS.vb in the original): the
 * same "create + assign" shape Step2Zones.tsx already had. Used to only
 * create outlet points here, with assignment (and aquifers, both creating
 * them and assigning them) living in a separate "Topology" step 4 — split
 * apart since "create this entity" and "assign it to sub-basins" are
 * really the same task in two motions, not two different phases; aquifers
 * moved to step 4 on their own for the same reason (see its own header
 * comment).
 */
export function Step3Outlets({
  project,
  gisLayer,
  onChange,
}: {
  project: EvalHidProject;
  gisLayer: SubBasinLayer | null;
  onChange: (project: EvalHidProject) => void;
}) {
  const t = useT();
  const outletName = (cod: number | null) =>
    cod == null ? "—" : (project.outletPoints.find((p) => p.outletCode === cod)?.name ?? `#${cod}`);
  const hasRunoffZones = project.zones.some((z) => z.modelTypeCode != null);
  const canReplicate = hasUnreplicatedZones(project);

  return (
    <div>
      <h3>3. {t("step3.title")}</h3>

      {hasRunoffZones && (
        <p>
          <button
            className="button"
            disabled={!canReplicate}
            title={canReplicate ? undefined : t("step3.replicateZonesUpToDate")}
            onClick={() => onChange(replicateZonesAsOutletPoints(project))}
          >
            {t("step3.replicateZonesButton")}
          </button>{" "}
          <InfoIcon text={t("step3.replicateZonesHelp")} />
        </p>
      )}

      <OutletPointsSection project={project} onChange={onChange} />

      {project.outletPoints.length > 0 && (
        <TopologyAssignmentSection
          title={t("step4.surfaceOutlet")}
          gisLayer={gisLayer}
          project={project}
          isAssigned={(c) => c.outletCode != null}
          valueLabel={(c) => outletName(c.outletCode)}
          options={project.outletPoints.map((p) => ({
            value: p.outletCode,
            label: p.name,
          }))}
          buttonText={t("outletPointsPanel.assignOutlet")}
          unassignValue={null}
          onAssign={(codes, value) => onChange(assignSurfaceOutletInBulk(project, codes, value))}
        />
      )}
    </div>
  );
}
